"use client";

import { useEffect } from "react";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
import { Button } from "@/components/ui/button";
export default function HowWePracticeMedicine() {
  const {
    ref: headingRef,
    isVisible: headingVisible
  } = useRevealOnScroll({
    once: true,
    threshold: 0.3
  });
  const {
    ref: step1Ref,
    isVisible: step1Visible
  } = useRevealOnScroll({
    once: true,
    threshold: 0.3
  });
  const {
    ref: step2Ref,
    isVisible: step2Visible
  } = useRevealOnScroll({
    once: true,
    threshold: 0.3
  });
  const {
    ref: step3Ref,
    isVisible: step3Visible
  } = useRevealOnScroll({
    once: true,
    threshold: 0.3
  });
  useEffect(() => {
    if (!document.querySelector('link[href*="calendly"]')) {
      const link = document.createElement("link");
      link.href = "https://assets.calendly.com/assets/external/widget.css";
      link.rel = "stylesheet";
      document.head.appendChild(link);
    }
    if (!document.querySelector('script[src*="calendly"]')) {
      const script = document.createElement("script");
      script.src = "https://assets.calendly.com/assets/external/widget.js";
      script.type = "text/javascript";
      script.async = true;
      document.body.appendChild(script);
    }
  }, []);
  const handleScheduleClick = () => {
    if (typeof window !== "undefined" && (window as any).Calendly) {
      (window as any).Calendly.initPopupWidget({
        url: "https://calendly.com/mfloresdo/30min"
      });
    }
  };
  return <section className="bg-background py-8 sm:py-8">
      <div className="container max-w-4xl mx-auto px-4 sm:px-6">
        <h2 ref={headingRef} className={`text-3xl sm:text-4xl font-display font-bold text-center text-foreground mb-12 transition-all duration-700 ease-[var(--ease-out-back)] ${headingVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
          How We Practice Medicine
        </h2>

        <div className="space-y-10">
          <div ref={step1Ref} className={`transition-all duration-700 ease-[var(--ease-out-back)] delay-100 ${step1Visible ? "opacity-100 translate-x-0" : "opacity-0 -translate-x-8"}`}>
            <div className="flex items-start gap-4">
              <div className="flex-shrink-0 w-10 h-10 rounded-full bg-primary text-primary-foreground flex items-center justify-center font-bold text-lg">
                1
              </div>
              <div className="leading-relaxed text-base sm:text-lg pt-1.5">
                <p className="text-foreground font-semibold mb-2">We Monitor, Adjust, and Optimize</p>
                <p className="text-foreground">
                  Every patient begins with a comprehensive intake that reviews symptoms, medical history, lifestyle factors, and relevant lab work. We're not guessing—and we're not treating numbers in isolation.
                </p>
              </div>
            </div>
          </div>

          <div ref={step2Ref} className={`transition-all duration-700 ease-[var(--ease-out-back)] delay-200 ${step2Visible ? "opacity-100 translate-x-0" : "opacity-0 -translate-x-8"}`}>
            <div className="flex items-start gap-4">
              <div className="flex-shrink-0 w-10 h-10 rounded-full bg-primary text-primary-foreground flex items-center justify-center font-bold text-lg">
                2
              </div>
              <div className="leading-relaxed text-base sm:text-lg pt-1.5">
                <p className="text-foreground font-semibold mb-2">
                  Treatment Is Individualized
                </p>
                <p className="text-foreground">
                  If treatment is appropriate, we design a plan specific to your physiology, goals, and risk profile. That may include hormone optimization, metabolic therapy, lifestyle interventions, or a combination—never cookie-cutter dosing.
                </p>
              </div>
            </div>
          </div>

          <div ref={step3Ref} className={`transition-all duration-700 ease-[var(--ease-out-back)] delay-300 ${step3Visible ? "opacity-100 translate-x-0" : "opacity-0 -translate-x-8"}`}>
            <div className="flex items-start gap-4">
              <div className="flex-shrink-0 w-10 h-10 rounded-full bg-primary text-primary-foreground flex items-center justify-center font-bold text-lg">
                3
              </div>
              <div className="leading-relaxed text-base sm:text-lg pt-1.5">
                <p className="text-foreground font-semibold mb-2">
                  We Monitor, Adjust, and Optimize
                </p>
                <p className="text-foreground">
                  We follow patients longitudinally with lab monitoring, symptom tracking, and adjustments over time. Health optimization is a process, not a one-time event.
                </p>
              </div>
            </div>
          </div>
        </div>

        <div className="flex flex-col items-center mt-12">
          <Button onClick={handleScheduleClick} className="bg-accent text-accent-foreground hover:bg-accent-hover px-8 py-3 rounded-sm font-semibold text-base transition-all duration-[var(--duration-fast)] hover:shadow-medium hover:-translate-y-0.5">
            Schedule a Consultation
          </Button>
          <p className="text-sm text-center text-muted-dark mt-3">
            Medical evaluation first. No pressure. Treatment only if appropriate.
          </p>
        </div>
      </div>
    </section>;
}